<script lang="ts">
  import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { ApiError } from "../../../../../../../../../../../api/kehrblatt/client"
  import { createContactOrder } from "../../../../../../../../../../../api/kehrblatt/contact-orders"
  import { createScheduleEvent } from "../../../../../../../../../../../api/kehrblatt/schedule-events"
  import {
    createWorkOrder,
    fetchWorkOrders,
  } from "../../../../../../../../../../../api/kehrblatt/work-orders"
  import { runScheduleEventActionWithValidation } from "../../../../../../_logic/run-schedule-event-action-with-validation"
  import {
    buildAppointmentContactOrderPayload,
    buildAppointmentCreatePayload,
    resolveAppointmentWorkOrder,
  } from "./_logic/payload"
  import {
    buildSelectionForCreatedAppointmentWorkOrder,
    buildWorkOrderCreatePayloadFromDraft,
    formatOpenWorkOrderWarningLabel,
    normalizeId,
  } from "../../../../_shared/_logic/work-order-draft"
  import type {
    AppointmentAnnouncementOutput,
    AppointmentCreateInit,
    AppointmentGeneralOutput,
    AppointmentPlanningOutput,
  } from "../../../../../../_logic/create-appointment-contract"
  import StepActionCTA from "../../../../../../../../../_shared/_modules/step-action-cta"

  type Props = {
    init: AppointmentCreateInit
    general: AppointmentGeneralOutput
    planning: AppointmentPlanningOutput
    announcement: AppointmentAnnouncementOutput
    disabled?: boolean
    disabledReason?: string | null
    onWorkOrderCreated?: (output: AppointmentGeneralOutput) => Promise<void> | void
    onCreated?: (event: ScheduleEvent) => Promise<void> | void
    onClose?: () => void
    onError?: (message: string | null) => void
  }

  const {
    init,
    general,
    planning,
    announcement,
    disabled = false,
    disabledReason = null,
    onWorkOrderCreated = async () => {},
    onCreated = async () => {},
    onClose = () => {},
    onError = () => {},
  }: Props = $props()

  let saving = $state(false)

  const resolveErrorMessage = (error: unknown, fallback: string) => {
    if (error instanceof ApiError && error.message?.trim()) return error.message.trim()

    if (error instanceof Error && error.message?.trim()) return error.message.trim()

    return fallback
  }

  const confirmExistingOpenOrdersForAddress = async ({
    propertyId,
    propertyUnitId,
  }: {
    propertyId: string
    propertyUnitId?: string | null
  }) => {
    if (!propertyId || !init.organizationId) return true

    try {
      const openOrders =
        (await fetchWorkOrders({
          organizationId: init.organizationId,
          propertyId,
          propertyUnitId: propertyUnitId || undefined,
          status: "OPEN",
          limit: 20,
        })) ?? []

      if (!openOrders.length) return true

      const previewText = openOrders
        .slice(0, 3)
        .map((order) => formatOpenWorkOrderWarningLabel({ order, properties: init.properties }))
        .join(" | ")
      const approved = await newApproval({
        title: "Offene Aufträge für diese Adresse",
        description: `Es gibt bereits ${openOrders.length} offene Auftrag${openOrders.length === 1 ? "" : "e"} für diese Adresse: ${previewText}. Auftrag trotzdem anlegen?`,
        approvalLabel: "Trotzdem anlegen",
        declineLabel: "Abbrechen",
      })

      return Boolean(approved)
    } catch (err) {
      console.error("open_work_orders_lookup_failed_for_appointment_create", err)
      newNotification({
        class: "error",
        html: "Offene Aufträge für diese Adresse konnten nicht geprüft werden.",
      })

      return false
    }
  }

  const ensureWorkOrderForAppointment = async () => {
    if (general.workOrderSource !== "new") return general

    const draft = general.newWorkOrderDraft

    if (!draft) {
      throw new Error("Bitte die Auftragsdaten prüfen.")
    }

    const propertyId = normalizeId(draft.property_id)
    const propertyUnitId = normalizeId(draft.property_unit_id) || null
    const approvedAddress = await confirmExistingOpenOrdersForAddress({
      propertyId,
      propertyUnitId,
    })

    if (!approvedAddress) return null

    const createdWorkOrder = await createWorkOrder(buildWorkOrderCreatePayloadFromDraft(draft))
    const selectedWorkOrder = buildSelectionForCreatedAppointmentWorkOrder({
      workOrder: createdWorkOrder,
      properties: init.properties,
      scheduleEvents: init.scheduleEvents,
    })
    const output: AppointmentGeneralOutput = {
      ...general,
      workOrderSource: "existing",
      workOrderId: createdWorkOrder.id,
      selectedWorkOrder,
      newWorkOrderDraft: null,
    }
    await onWorkOrderCreated(output)

    return output
  }

  const run = async () => {
    if (saving) return

    onError(null)

    if (disabled) {
      onError(disabledReason ?? "Bitte alle Pflichtfelder ausfüllen.")

      return
    }

    const pendingWorkOrderId =
      general.workOrderSource === "new" ? "__pending_new_work_order__" : null
    const preValidation = buildAppointmentCreatePayload({
      init,
      general,
      planning,
      now: new Date(),
      workOrderIdOverride: pendingWorkOrderId,
    })

    if (preValidation.ok === false) {
      onError(preValidation.reason)

      return
    }

    saving = true
    try {
      const createdNewWorkOrder = general.workOrderSource === "new"
      const effectiveGeneral = await ensureWorkOrderForAppointment()

      if (!effectiveGeneral) return

      const validation = buildAppointmentCreatePayload({
        init,
        general: effectiveGeneral,
        planning,
        now: new Date(),
      })

      if (validation.ok === false) {
        onError(validation.reason)

        return
      }

      const actionResult = await runScheduleEventActionWithValidation({
        validationRequest: {
          operation: "CREATE",
          surface: "CREATE_APPOINTMENT",
          create_payload: validation.payload,
        },
        executeDirect: () => createScheduleEvent(validation.payload),
      })

      if (actionResult.status !== "completed") return

      const createdEvent = actionResult.value as ScheduleEvent
      let completedEvent = createdEvent

      if (announcement.enabled) {
        const workOrder = resolveAppointmentWorkOrder({ init, general: effectiveGeneral })

        if (!workOrder?.case_id) {
          throw new Error("Zum Auftrag wurde kein Vorgang gefunden.")
        }

        const contactOrder = await createContactOrder(
          workOrder.case_id,
          buildAppointmentContactOrderPayload({
            init,
            event: createdEvent,
            general: effectiveGeneral,
            planning,
            announcement,
          }),
        )
        completedEvent = {
          ...createdEvent,
          status: "RESERVED" as ScheduleEventStatus,
          contact_order_id: contactOrder.id,
        }
      }

      newNotification({
        class: "success",
        html: createdNewWorkOrder
          ? announcement.enabled
            ? "Auftrag, Termin und Ankündigungsplan angelegt."
            : "Auftrag und Termin angelegt."
          : announcement.enabled
            ? "Termin und Ankündigungsplan angelegt."
            : "Termin angelegt.",
      })
      await onCreated(completedEvent)
      onClose()
    } catch (error) {
      console.error("appointment_create_flow_failed", error)
      newNotification({
        class: "error",
        html: resolveErrorMessage(error, "Termin konnte nicht angelegt werden."),
      })
    } finally {
      saving = false
    }
  }
</script>

<StepActionCTA
  type="primaryButton"
  content={saving ? "Lege an…" : "Termin anlegen"}
  disabled={saving || disabled}
  disabledReason={saving ? "Termin wird gerade angelegt." : disabledReason}
  onClick={run}
/>
